import React, { useEffect, useState } from "react";
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Chip, Tooltip,
} from "@mui/material";
import {
  Dashboard, Hub, Cable, Receipt, History, Palette, Settings, Groups,
  Add as AddIcon,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const MENU_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: Dashboard },
  { id: "connections", label: "Canais", icon: Cable },
  { id: "tenants", label: "Tenants", icon: Groups, centralOnly: true },
  { id: "hubs", label: "HUBs Locais", icon: Hub },
  { id: "billing", label: "Billing", icon: Receipt },
  { id: "logs", label: "Logs", icon: History },
  { id: "whitelabel", label: "White Label", icon: Palette },
  { id: "settings", label: "Configuracoes", icon: Settings },
];

/*
 * Selo CENTRAL/LOCAL: diz, so de olhar, em que instancia se esta.
 * O papel vem do backend (GET /hub/role), que usa a mesma regra da delegacao:
 * delega ao Central = LOCAL; nao delega = CENTRAL. Falhou a busca = sem selo.
 */
const SELOS = {
  central: {
    label: "CENTRAL",
    cor: "#10B981",
    dica: "HUB Central: esta instancia nao delega a gestao a outro HUB.",
  },
  local: {
    label: "LOCAL",
    cor: "#3B82F6",
    dica: "HUB Local: esta instancia delega a gestao ao HUB Central.",
  },
};

function usePapelDaInstancia() {
  const [papel, setPapel] = useState(null);
  useEffect(() => {
    let ativo = true;
    hubApi
      .getRole()
      .then(({ data }) => {
        const role = data?.role;
        if (ativo && (role === "central" || role === "local")) setPapel(role);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, []);
  return papel;
}

export default function Sidebar({ currentPage, onPageChange, onOpenHub, tenant }) {
  const papel = usePapelDaInstancia();
  const selo = papel ? SELOS[papel] : null;

  return (
    <Box
      sx={{
        width: 240,
        minHeight: "100vh",
        bgcolor: "#0D1117",
        borderRight: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          component="img"
          src="/gateway-icon.png"
          alt="OtimizApp HUB"
          sx={{ width: 36, height: 36, borderRadius: 2, objectFit: "cover" }}
        />
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#fff", lineHeight: 1.2 }}>
              OtimizAPP
            </Typography>
            {selo && (
              <Tooltip title={selo.dica} placement="right" arrow>
                <Chip
                  label={selo.label}
                  size="small"
                  data-testid="selo-papel-hub"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: "#fff",
                    bgcolor: selo.cor,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: 11, color: "#6B7280", lineHeight: 1.2 }}>
            Connect
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />

      {/* Tenant */}
      {tenant && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1 }}>
            Tenant
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#D1D5DB", fontWeight: 500 }}>
            {tenant.name}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />

      {/* Menu */}
      <List sx={{ flex: 1, py: 1 }}>
        {MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;

          return (
            <ListItemButton
              key={item.id}
              selected={isActive}
              onClick={() => onPageChange(item.id)}
              sx={{
                mx: 1, borderRadius: 2, mb: 0.5,
                "&.Mui-selected": {
                  bgcolor: "rgba(107, 72, 255, 0.15)",
                  "&:hover": { bgcolor: "rgba(107, 72, 255, 0.2)" },
                },
                "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Icon sx={{ fontSize: 20, color: isActive ? "#6B48FF" : "#6B7280" }} />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  color: isActive ? "#fff" : "#9CA3AF",
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />

      {/* Nova Conexão */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={onOpenHub}
          sx={{
            borderRadius: 2,
            bgcolor: "rgba(107, 72, 255, 0.1)",
            border: "1px solid rgba(107, 72, 255, 0.3)",
            "&:hover": { bgcolor: "rgba(107, 72, 255, 0.2)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <AddIcon sx={{ fontSize: 18, color: "#6B48FF" }} />
          </ListItemIcon>
          <ListItemText
            primary="Nova Conexao"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: "#6B48FF" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );
}
